// The shop map and where a pallet beacon is on it (jobq-api beacon_location.py + GET /shop_map,
// 2026-10-20). Plan: ~/.claude/plans/proud-dazzling-fern-jobq-design.md sections D and E.
//
// Frame: feet, x east, y north, origin = the main shop's SW interior corner. The metal room is at
// negative x. SVG's y runs DOWN, so every point goes through svgPt (y-flip) on its way to the screen.
//
// Advisory like the rest of the beacon UI: the calls never throw ({ok, status, body, message}).

import { beaconFetch, lastSeenLabel, locationFresh, locationPlace } from './beacons';
import { shopClock } from './shopDate';

/** The geometry document's bounds when it has none: [x0, y0, x1, y1] in feet. */
export const DEFAULT_BOUNDS = Object.freeze([-82, -6, 122, 75]);

// ── API ──

let cache = null; // { rev: number|null, promise }

/**
 * GET /shop_map, cached for the page's life: {ok, status, body, message}. `mapRev` = a
 * location's map_rev: a cached map of another rev is read again. `diagnostics` / `coverage` ask
 * for the extras (the /shop-map page) and always read; `force` reads again. A failed read is not
 * cached.
 */
export function loadShopMap(mapRev = null, { diagnostics = false, coverage = false, force = false } = {}) {
  const extras = diagnostics || coverage;
  const fresh = cache && !force && !extras && (mapRev === null || mapRev === undefined || cache.rev === null
    || Number(cache.rev) === Number(mapRev));
  if (fresh) return cache.promise;
  const q = [diagnostics ? 'diagnostics=1' : '', coverage ? 'coverage=1' : ''].filter(Boolean).join('&');
  const entry = { rev: null, promise: null };
  entry.promise = beaconFetch(`/shop_map${q ? `?${q}` : ''}`).then((r) => {
    if (r.ok && r.body && r.body.map) entry.rev = r.body.map.rev;
    else if (cache === entry) cache = null;
    return r;
  });
  cache = entry;
  return entry.promise;
}

export function invalidateShopMap() {
  cache = null;
}

export function __resetShopMapForTests() {
  cache = null;
}

/**
 * PUT /shop_map {rev, pin, name?, geometry?, settings?, anchors?, gateways?, zones?}. Resolves the
 * answer plus `stale` (409 stale_map: someone else saved first). The cached map is dropped.
 */
export async function saveShopMap(payload) {
  const r = await beaconFetch('/shop_map', { method: 'PUT', body: JSON.stringify(payload) });
  cache = null;
  const d = r.body && r.body.detail;
  const stale = r.status === 409 && (!d || typeof d !== 'object' || !d.error || d.error === 'stale_map');
  return { ...r, stale };
}

/** POST /shop_map/model/refit {pin}. */
export function refitShopModel(pin) {
  return beaconFetch('/shop_map/model/refit', { method: 'POST', body: JSON.stringify({ pin }) });
}

/** GET /shop_map/calibration: {points, summary}. */
export function loadCalibration() {
  return beaconFetch('/shop_map/calibration');
}

/** While a calibration point waits for its first scan, GET /shop_map/calibration is read every CALIB_POLL_MS, at most CALIB_POLL_FOR_MS. */
export const CALIB_POLL_MS = 5000;
export const CALIB_POLL_FOR_MS = 2 * 60 * 1000;

const hasEstimate = (r) => !!r && r.est_x_ft !== null && r.est_x_ft !== undefined
  && r.est_y_ft !== null && r.est_y_ft !== undefined;

/**
 * One calibration point as the walk table shows it (jobq-api calibration_report): the point
 * carries median_err_ft and truth_area; inside_r80 / area_ok and the estimate live on each of its
 * `readings`. The table takes them from the LAST reading that has an estimate.
 * -> {errFt, insideR80, areaOk, est: {x, y}|null, readings: count, truthArea}
 */
export function calibrationRow(p) {
  const reads = p && Array.isArray(p.readings) ? p.readings : [];
  const last = [...reads].reverse().find(hasEstimate) || null;
  const errFt = p && p.median_err_ft !== undefined && p.median_err_ft !== null ? Number(p.median_err_ft) : null;
  return {
    errFt,
    insideR80: last && typeof last.inside_r80 === 'boolean' ? last.inside_r80 : null,
    areaOk: last && typeof last.area_ok === 'boolean' ? last.area_ok : null,
    est: last ? { x: Number(last.est_x_ft), y: Number(last.est_y_ft) } : null,
    readings: reads.length,
    truthArea: (p && p.truth_area) || null,
  };
}

/** Whether an open calibration point (not ended) has no reading yet: the page keeps re-reading. */
export function calibrationAwaiting(calib) {
  const pts = calib && Array.isArray(calib.points) ? calib.points : [];
  return pts.some((p) => p && !p.ended_at && !(Array.isArray(p.readings) && p.readings.length > 0));
}

/** POST /shop_map/calibration {beacon_id, x_ft, y_ft, z_ft?, notes?, pin}: also queues a short Find. */
export function addCalibrationPoint(body) {
  return beaconFetch('/shop_map/calibration', { method: 'POST', body: JSON.stringify(body) });
}

/** PATCH /shop_map/calibration/{id} (e.g. {end: true, pin}). */
export function patchCalibrationPoint(pointId, body) {
  return beaconFetch(`/shop_map/calibration/${encodeURIComponent(pointId)}`, { method: 'PATCH', body: JSON.stringify(body) });
}

/**
 * GET /beacons/{id}/location?history=&reading_id=&heat=1 (read only). `history` is sent whenever it
 * is given, 0 included (the API's default is 20 readings: history 0 skips that read).
 */
export function loadLocation(beaconId, { readingId = null, heat = false, history = null } = {}) {
  const q = [];
  if (history !== null && history !== undefined) q.push(`history=${encodeURIComponent(history)}`);
  if (readingId !== null && readingId !== undefined) q.push(`reading_id=${encodeURIComponent(readingId)}`);
  if (heat) q.push('heat=1');
  return beaconFetch(`/beacons/${encodeURIComponent(beaconId)}/location${q.length ? `?${q.join('&')}` : ''}`);
}

// ── Grids (the editor's coverage overlay): {x0, y0, cell_ft, cols, rows, data: base64(uint8, rows south -> north)} ──

function b64ToBytes(s) {
  try {
    const bin = atob(String(s));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
    return out;
  } catch (_) {
    return null;
  }
}

/**
 * A heat (or coverage) grid from the API -> {x0, y0, cell, cols, rows, data: Uint8Array, max}; null
 * when it is missing or doesn't add up. Row 0 is the SOUTH edge (y0). Already decoded: returned as is.
 */
export function decodeHeat(h) {
  if (!h || typeof h !== 'object') return null;
  if (h.data instanceof Uint8Array && Number.isFinite(h.max) && h.cell > 0) return h;
  const cols = Number(h.cols);
  const rows = Number(h.rows);
  const cell = Number(h.cell !== undefined ? h.cell : (h.cell_ft !== undefined ? h.cell_ft : 2));
  const x0 = Number(h.x0);
  const y0 = Number(h.y0);
  if (![cols, rows].every((n) => Number.isInteger(n) && n > 0) || !(cell > 0) || !Number.isFinite(x0) || !Number.isFinite(y0)) {
    return null;
  }
  const data = h.data instanceof Uint8Array ? h.data : (typeof h.data === 'string' ? b64ToBytes(h.data) : null);
  if (!data || data.length < cols * rows) return null;
  let max = 0;
  for (let i = 0; i < cols * rows; i += 1) if (data[i] > max) max = data[i];
  return { x0, y0, cell, cols, rows, data, max };
}

/** A decoded grid's value at a shop point (0 outside it). */
export function heatValueAt(grid, x, y) {
  if (!grid) return 0;
  const c = Math.floor((x - grid.x0) / grid.cell);
  const r = Math.floor((y - grid.y0) / grid.cell);
  if (c < 0 || r < 0 || c >= grid.cols || r >= grid.rows) return 0;
  return grid.data[r * grid.cols + c];
}

/** Where a decoded grid sits in SVG units: {x, y (top), width, height}. */
export function heatRect(grid) {
  const w = grid.cols * grid.cell;
  const h = grid.rows * grid.cell;
  return { x: grid.x0, y: -(grid.y0 + h), width: w, height: h };
}

const isJsdom = () => typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '');

/** RGBA for one cell. 'heat': 0 = off the floor / nothing, then yellow -> red. 'coverage': anchor count. */
export function cellColor(v, palette = 'heat') {
  if (palette === 'coverage') {
    if (v >= 3) return [25, 160, 70, 70];
    if (v >= 1) return [240, 150, 20, 90];
    return [220, 40, 40, 100];
  }
  if (!v) return [0, 0, 0, 0];
  const t = v / 255;
  return [Math.round(255 - 35 * t), Math.round(215 - 195 * t), 0, Math.round(35 + 185 * t)];
}

/**
 * A decoded grid as a PNG data URL for an SVG <image> (north at the top). Null when there is no
 * canvas (jsdom, an old browser): the map still draws without it.
 */
export function heatToDataUrl(grid, { palette = 'heat' } = {}) {
  if (!grid || typeof document === 'undefined' || isJsdom()) return null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = grid.cols;
    canvas.height = grid.rows;
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return null;
    const img = ctx.createImageData(grid.cols, grid.rows);
    for (let r = 0; r < grid.rows; r += 1) {
      const src = (grid.rows - 1 - r) * grid.cols; // image row 0 = the north edge
      for (let c = 0; c < grid.cols; c += 1) {
        const [R, G, B, A] = cellColor(grid.data[src + c], palette);
        const o = (r * grid.cols + c) * 4;
        img.data[o] = R; img.data[o + 1] = G; img.data[o + 2] = B; img.data[o + 3] = A;
      }
    }
    ctx.putImageData(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch (_) {
    return null;
  }
}

// ── Geometry ──

/** Shop feet -> SVG units: the same x, y flipped (shop y runs north, SVG y runs down). */
export function svgPt(x, y) {
  return { x: Number(x), y: -Number(y) };
}

/** SVG units -> shop feet (the inverse of svgPt). */
export function shopPt(sx, sy) {
  return { x: Number(sx), y: -Number(sy) };
}

/** "x,y x,y …" for an SVG polygon / polyline from shop points. */
export function svgPoints(pts) {
  return (Array.isArray(pts) ? pts : [])
    .filter((p) => Array.isArray(p) && Number.isFinite(Number(p[0])) && Number.isFinite(Number(p[1])))
    .map(([x, y]) => `${Number(x)},${-Number(y)}`).join(' ');
}

/** The map's bounds [x0, y0, x1, y1] (the geometry's, else DEFAULT_BOUNDS). */
export function mapBounds(geometry) {
  const b = geometry && Array.isArray(geometry.bounds) ? geometry.bounds.map(Number) : null;
  if (b && b.length === 4 && b.every(Number.isFinite) && b[2] > b[0] && b[3] > b[1]) return b;
  return [...DEFAULT_BOUNDS];
}

/** An SVG viewBox string for a box {x0, y0, x1, y1} (or [x0, y0, x1, y1]) in shop feet. */
export function viewBoxOf(box) {
  const [x0, y0, x1, y1] = Array.isArray(box) ? box : [box.x0, box.y0, box.x1, box.y1];
  return `${x0} ${-y1} ${x1 - x0} ${y1 - y0}`;
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * The zoomed view for a location: its r80 circle with room around it, at least minW x minH feet
 * (3:2), kept inside the map's bounds. The whole map when the location has no point.
 * Returns {x0, y0, x1, y1} in shop feet.
 */
export function focusViewBox(loc, bounds = DEFAULT_BOUNDS, { minW = 60, minH = 40, pad = 1.3 } = {}) {
  const [bx0, by0, bx1, by1] = bounds;
  const whole = { x0: bx0, y0: by0, x1: bx1, y1: by1 };
  if (!loc || !Number.isFinite(Number(loc.x_ft)) || !Number.isFinite(Number(loc.y_ft))
    || loc.x_ft === null || loc.y_ft === null) return whole;
  const cx = Number(loc.x_ft);
  const cy = Number(loc.y_ft);
  const r = Math.max(Number(loc.r80_ft) || 0, 4);
  let w = Math.max(minW, 2 * r * pad);
  let h = Math.max(minH, 2 * r * pad);
  const aspect = minW / minH;
  if (w / h < aspect) w = h * aspect; else h = w / aspect;
  const span = (lo, hi, size, c) => {
    if (size >= hi - lo) return [lo, hi];
    const a = clamp(c - size / 2, lo, hi - size);
    return [a, a + size];
  };
  const [x0, x1] = span(bx0, bx1, w, cx);
  const [y0, y1] = span(by0, by1, h, cy);
  return { x0, y0, x1, y1 };
}

// ── Words ──

const PLACE_SEP = ' · ';

function at(iso, now) {
  const clock = shopClock(iso, new Date(now));
  const rel = lastSeenLabel(iso, now);
  return clock ? `${clock} (${rel})` : rel;
}

/** The gateway that hears the beacon best right now ({gateway_id, rssi, heard_at}) or null. */
export function strongestGateway(loc) {
  const heard = loc && Array.isArray(loc.heard_now) ? loc.heard_now.filter((h) => h && h.gateway_id) : [];
  if (!heard.length) return null;
  return heard.reduce((a, b) => ((Number(b.rssi) > Number(a.rssi)) ? b : a));
}

/**
 * One line saying where a beacon is (jobq-api LocationView). Times are the shop's (Central):
 *   "Metal room · near Rack A · last seen 10:02 AM (2 h ago)"   a scan
 *   "Metal room · near Rack A · now"                             a scan in the last 2 min / after this Find
 *   "Plugged in at Laser"                                        docked
 *   "Updating location…"                                         a Find asked for a scan; not in yet
 *   "Rough: heard by laser-pi 3 min ago"                         gateways only (older firmware)
 *   "No location yet (firmware 0.7)"                             nothing yet: firmware can't scan
 *   "Before it went on this pallet: Metal room · 10:02 AM (…)"   the last scan predates the assignment
 *   "Not heard since 10:02 AM (2 h ago)"                         nobody hears it, no scan
 * `beacon` (optional) = the beacon view, for last_seen_at / firmware_version. '' when nothing to say.
 * Inside a Find there is no beacon view: the location's own firmware_version is used when the API
 * sends it (it may add it to LocationView); without it, or without a last_seen_at, the wording stays
 * generic ("No location yet (its firmware can't scan)", never a made-up "Not heard since").
 */
export function locationCaption(loc, now = Date.now(), beacon = null) {
  if (!loc || typeof loc !== 'object') return '';
  const nowMs = Number(now);
  if (loc.best === 'dock') return `Plugged in at ${loc.dock_station || (beacon && beacon.docked_station) || 'the dock'}`;
  const place = locationPlace(loc);
  // a zone answer names its second zone itself; an area answer adds its alternative here
  const placeAlt = place && loc.alt_label && !(loc.zone && loc.zone.name) ? `${place} (or ${loc.alt_label})` : place;
  if (loc.awaiting_scan) {
    return place && loc.scanned_at
      ? `Updating location… last seen ${place}, ${shopClock(loc.scanned_at, new Date(nowMs))}`
      : 'Updating location…';
  }
  if (place && loc.before_assign) {
    return `Before it went on this pallet: ${place}${loc.scanned_at ? `${PLACE_SEP}${at(loc.scanned_at, nowMs)}` : ''}`;
  }
  if (loc.best === 'scan' && place) {
    if (locationFresh(loc, nowMs)) return `${placeAlt}${PLACE_SEP}now`;
    return `${placeAlt}${loc.scanned_at ? `${PLACE_SEP}last seen ${at(loc.scanned_at, nowMs)}` : ''}`;
  }
  if (loc.best === 'gateways' || loc.rough) {
    const g = strongestGateway(loc);
    if (g) return `Rough: heard by ${g.gateway_id} ${lastSeenLabel(g.heard_at, nowMs)}`;
    if (place) return `Rough: ${place}${loc.scanned_at ? `${PLACE_SEP}${at(loc.scanned_at, nowMs)}` : ''}`;
  }
  const heard = Array.isArray(loc.heard_now) && loc.heard_now.length > 0;
  const lastSeen = (beacon && beacon.last_seen_at) || loc.last_seen_at || null;
  if (!heard && lastSeen) return `Not heard since ${at(lastSeen, nowMs)}`;
  if (loc.firmware_scans === false) {
    const fw = loc.firmware_version || (beacon && beacon.firmware_version) || null;
    return fw ? `No location yet (firmware ${fw})` : "No location yet (its firmware can't scan)";
  }
  return 'No location yet';
}

// ── Pallet zones (shop_zones kind 'pallet': where a Find says a pallet is) ──

/** The map's active pallet zones with a drawable polygon. */
export function palletZones(zones) {
  return (Array.isArray(zones) ? zones : []).filter((z) => z && z.kind === 'pallet' && z.is_active !== false
    && Array.isArray(z.polygon) && z.polygon.length >= 3);
}

/** A polygon's bounding box [x0, y0, x1, y1] (feet), or null. */
export function polyBox(poly) {
  const pts = (Array.isArray(poly) ? poly : []).map((p) => [Number(p[0]), Number(p[1])])
    .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y));
  if (!pts.length) return null;
  return [Math.min(...pts.map((p) => p[0])), Math.min(...pts.map((p) => p[1])),
    Math.max(...pts.map((p) => p[0])), Math.max(...pts.map((p) => p[1]))];
}

/**
 * The pallet zones a LocationView names, matched on the map: {best, second} (each a zone or null).
 * By zone_id, else by name (a zone renamed since the estimate keeps its id; one re-made keeps its name).
 */
export function locationZones(loc, zones) {
  const list = palletZones(zones);
  const find = (z) => {
    if (!z) return null;
    return list.find((m) => z.zone_id !== null && z.zone_id !== undefined && m.zone_id === z.zone_id)
      || list.find((m) => z.name && String(m.name).toLowerCase() === String(z.name).toLowerCase()) || null;
  };
  return { best: find(loc && loc.zone), second: find(loc && loc.zone2) };
}

/**
 * The zoomed view on a location's zones: the box around the best zone (and the second one when
 * shown), padded, at least minW x minH feet (3:2), inside the map's bounds. The whole map without.
 */
export function zoneFocusBox(loc, zones, bounds = DEFAULT_BOUNDS, { minW = 60, minH = 40, pad = 6 } = {}) {
  const [bx0, by0, bx1, by1] = bounds;
  const { best, second } = locationZones(loc, zones);
  const boxes = [best, second].filter(Boolean).map((z) => polyBox(z.polygon)).filter(Boolean);
  if (!boxes.length) return { x0: bx0, y0: by0, x1: bx1, y1: by1 };
  const x0 = Math.min(...boxes.map((b) => b[0])) - pad;
  const y0 = Math.min(...boxes.map((b) => b[1])) - pad;
  const x1 = Math.max(...boxes.map((b) => b[2])) + pad;
  const y1 = Math.max(...boxes.map((b) => b[3])) + pad;
  let w = Math.max(minW, x1 - x0);
  let h = Math.max(minH, y1 - y0);
  const aspect = minW / minH;
  if (w / h < aspect) w = h * aspect; else h = w / aspect;
  const span = (lo, hi, size, c) => {
    if (size >= hi - lo) return [lo, hi];
    const a = clamp(c - size / 2, lo, hi - size);
    return [a, a + size];
  };
  const [vx0, vx1] = span(bx0, bx1, w, (x0 + x1) / 2);
  const [vy0, vy1] = span(by0, by1, h, (y0 + y1) / 2);
  return { x0: vx0, y0: vy0, x1: vx1, y1: vy1 };
}

/**
 * "62 %" for a zone's probability (0..1), '' without one. Rounded DOWN, so a best zone under 0.7 never reads
 * "70 %" next to its second one; never 0 % or 100 % (a model is never sure).
 */
export function zonePct(z) {
  // a loudest-tag answer has p null: Number(null) is 0, which would read "1 %"
  const p = z && z.p !== null && z.p !== undefined ? Number(z.p) : NaN;
  if (!Number.isFinite(p)) return '';
  return `${Math.min(99, Math.max(1, Math.floor(p * 100 + 1e-9)))} %`;
}

/**
 * How strong a zone answer is: "62 %" under the location model, "-55 dBm" (the loudest heard tag of that zone)
 * under the loudest-tag rule (jobq-api zone_rule 'loudest_tag': its zones carry `tag` + `rssi`, p is null), ''.
 */
export function zoneStrength(z) {
  const pct = zonePct(z);
  if (pct) return pct;
  const r = z && z.rssi !== null && z.rssi !== undefined ? Number(z.rssi) : NaN;
  return Number.isFinite(r) ? `${Math.round(r)} dBm` : '';
}

/** The line a zone's label adds on the map: "62 %", or "tag 10 · -55 dBm" under the loudest-tag rule. */
export function zoneMapLine(z) {
  const strength = zoneStrength(z);
  if (!strength || zonePct(z)) return strength;
  return z.tag !== null && z.tag !== undefined ? `tag ${z.tag} · ${strength}` : strength;
}

/** Whether a LocationView's zone is the loudest-tag rule's (the zone of the loudest heard anchor tag). */
export function isLoudestTagAnswer(loc) {
  if (!loc) return false;
  if (loc.zone_rule) return loc.zone_rule === 'loudest_tag';
  const z = loc.zone;
  return !!z && (z.p === null || z.p === undefined) && z.tag !== null && z.tag !== undefined;
}

/** How a Find picks the zone (shop_maps.settings.zone_rule; absent = the location model). */
export const ZONE_RULES = [
  { value: 'loudest_tag', label: 'Loudest tag', help: "the zone of the loudest heard tag (two zones when their tags are within 3 dB)" },
  { value: 'model', label: 'Location model', help: 'where the location model puts the pallet, with a chance per zone' },
];

export function zoneRuleOf(settings) {
  return settings && settings.zone_rule === 'loudest_tag' ? 'loudest_tag' : 'model';
}

/** The map's settings with this zone rule (PUT replaces settings whole: everything else is kept). */
export function settingsWithZoneRule(settings, rule) {
  const s = { ...(settings || {}) };
  if (rule === 'loudest_tag') s.zone_rule = 'loudest_tag';
  else delete s.zone_rule;
  return s;
}

/** Whether a LocationView has a point to draw. */
export function hasPoint(loc) {
  return !!loc && loc.x_ft !== null && loc.x_ft !== undefined && loc.y_ft !== null && loc.y_ft !== undefined
    && Number.isFinite(Number(loc.x_ft)) && Number.isFinite(Number(loc.y_ft));
}

// ── The /shop-map page's rules ──

const placed = (a) => a && a.placed !== false && a.x_ft !== null && a.x_ft !== undefined
  && a.y_ft !== null && a.y_ft !== undefined;

/** Anchors a gateway (or beacon) has heard that have no place on the map yet, most recently heard first. */
export function heardNotPlaced(anchors) {
  return (Array.isArray(anchors) ? anchors : [])
    .filter((a) => !placed(a) && (a.last_heard_at || (Array.isArray(a.heard_by) && a.heard_by.length)))
    .sort((a, b) => String(b.last_heard_at || '').localeCompare(String(a.last_heard_at || '')));
}

/** Whether an anchor draws grey (switched off, or not placed). */
export function anchorIsGrey(a) {
  return !a || a.is_active === false || a.placed === false;
}

/** Snap to the map's 0.5 ft grid. */
export function snapFt(v, step = 0.5) {
  return Math.round(Number(v) / step) * step;
}

/**
 * The geometry textarea: {ok, geometry, error}. A geometry is an object with an `items` array (and
 * the bounds, if given, are 4 numbers); its size is held to the API's 256 KB.
 */
export function parseGeometryText(text) {
  const s = String(text || '');
  if (s.length > 256 * 1024) return { ok: false, geometry: null, error: 'The geometry is over 256 KB.' };
  let g;
  try {
    g = JSON.parse(s);
  } catch (e) {
    return { ok: false, geometry: null, error: `Not valid JSON: ${e.message}` };
  }
  if (!g || typeof g !== 'object' || Array.isArray(g)) return { ok: false, geometry: null, error: 'The geometry must be an object.' };
  if (!Array.isArray(g.items)) return { ok: false, geometry: null, error: 'The geometry needs an "items" list.' };
  if (g.bounds !== undefined && !(Array.isArray(g.bounds) && g.bounds.length === 4 && g.bounds.every((n) => Number.isFinite(n)))) {
    return { ok: false, geometry: null, error: '"bounds" must be [x0, y0, x1, y1] in feet.' };
  }
  const bad = g.items.findIndex((it) => !it || typeof it !== 'object' || typeof it.kind !== 'string');
  if (bad >= 0) return { ok: false, geometry: null, error: `Item ${bad + 1} needs a "kind".` };
  return { ok: true, geometry: g, error: null };
}

/**
 * The model's pair residuals for the model panel: [{key, gateway_id, anchor_no, rssi, pred, resid,
 * flagged}]. Reads diagnostics.residuals / diagnostics.pairs (GET /shop_map?diagnostics=1: a LIVE
 * refit from the links as they are now, never stored), else a model.pairs list. `flagged` is the
 * API's own per-row `flag` when it is a boolean (judged against the live fit's σ); otherwise
 * |resid| > 2σ, σ = the live fit's (diagnostics.fit_now.sigma_db), else the stored model's, else 7.
 */
export function residualRows(model, diagnostics) {
  const fitNow = diagnostics && diagnostics.fit_now;
  const sigma = [fitNow && fitNow.sigma_db, model && model.sigma_db].map(Number).find((v) => v > 0) || 7;
  const src = (diagnostics && (Array.isArray(diagnostics.residuals) ? diagnostics.residuals
    : (Array.isArray(diagnostics.pairs) ? diagnostics.pairs : null)))
    || (model && Array.isArray(model.pairs) ? model.pairs : []);
  const num = (...vs) => {
    const v = vs.find((x) => x !== null && x !== undefined && Number.isFinite(Number(x)));
    return v === undefined ? null : Number(v);
  };
  return src.map((p, i) => {
    const rssi = num(p.rssi, p.rssi_ewma, p.observed);
    const pred = num(p.pred, p.pred_dbm, p.predicted);
    const resid = num(p.resid, p.residual, p.resid_db, rssi !== null && pred !== null ? rssi - pred : null);
    const anchorNo = p.anchor_no !== undefined ? p.anchor_no : p.anchor;
    return {
      key: `${p.gateway_id || '?'}:${anchorNo}:${i}`, gateway_id: p.gateway_id || null, anchor_no: anchorNo,
      rssi, pred, resid,
      flagged: typeof p.flag === 'boolean' ? p.flag : (resid !== null && Math.abs(resid) > 2 * sigma),
    };
  });
}

const sameJson = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** A zone as PUT /shop_map takes it: {zone_id?, kind, name, polygon, sort}; never is_active. */
const zoneForPut = (z) => {
  const r = {};
  if (z.zone_id !== undefined && z.zone_id !== null) r.zone_id = z.zone_id;
  r.kind = z.kind;
  r.name = z.name;
  r.polygon = z.polygon;
  r.sort = z.sort === undefined || z.sort === null ? 0 : z.sort;
  return r;
};
const activeZones = (list) => (Array.isArray(list) ? list : []).filter((z) => z && z.is_active !== false).map(zoneForPut);

/**
 * The PUT /shop_map body for a draft: rev + pin always; name / geometry / settings when they
 * changed; the anchors and gateways that changed (each with ALL its fields: the API writes every
 * one, so a field left out is wiped, e.g. an anchor's notes).
 *
 * Zones are different: jobq-api takes a `zones` list as the COMPLETE new set (it switches every
 * active zone off, then back on only the listed ones; is_active is ignored). So when any zone
 * changed, was added or was switched off, `zones` = every draft zone still on, and a zone switched
 * off is simply left out. When none changed, `zones` is not sent at all (an empty or partial list
 * would switch the floor off).
 */
export function shopMapPayload(original, draft, pin) {
  const om = (original && original.map) || {};
  const out = { rev: om.rev, pin };
  if (draft.name !== undefined && draft.name !== om.name) out.name = draft.name;
  if (draft.geometry && !sameJson(draft.geometry, om.geometry)) out.geometry = draft.geometry;
  if (draft.settings && !sameJson(draft.settings, om.settings)) out.settings = draft.settings;
  const changed = (list, origList, key, fields) => {
    const byKey = new Map((origList || []).map((o) => [String(o[key]), o]));
    return (list || []).filter((x) => {
      const o = x[key] === undefined || x[key] === null ? null : byKey.get(String(x[key]));
      return !o || fields.some((f) => !sameJson(x[f], o[f]));
    }).map((x) => {
      const r = {};
      if (x[key] !== undefined && x[key] !== null) r[key] = x[key];
      fields.forEach((f) => { if (x[f] !== undefined) r[f] = x[f]; });
      return r;
    });
  };
  const anchors = changed(draft.anchors, original && original.anchors, 'anchor_no',
    ['name', 'x_ft', 'y_ft', 'z_ft', 'is_active', 'notes', 'zone_id']);
  const gateways = changed(draft.gateways, original && original.gateways, 'gateway_id', ['x_ft', 'y_ft', 'z_ft']);
  if (anchors.length) out.anchors = anchors;
  if (gateways.length) out.gateways = gateways;
  if (draft.zones !== undefined) {
    const zones = activeZones(draft.zones);
    if (!sameJson(zones, activeZones(original && original.zones))) out.zones = zones;
  }
  return out;
}

/** Whether a payload carries anything besides rev + pin. */
export function payloadHasChanges(p) {
  return !!p && Object.keys(p).some((k) => k !== 'rev' && k !== 'pin');
}
